import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "report", "submitBtn", "validateBtn", "modal"]

  connect() {
    this.lastImportData = null
    this.validationPassed = false
    this.resetListener = () => this.resetModal()
    this.modalTarget?.addEventListener('hidden.bs.modal', this.resetListener)
  }

  disconnect() {
    this.modalTarget?.removeEventListener('hidden.bs.modal', this.resetListener)
  }

  resetModal() {
    this.lastImportData = null
    this.validationPassed = false
    this.formTarget?.reset()
    if (this.reportTarget) {
      this.reportTarget.innerHTML = ''
    }
    this.setButtonIdle('submit')
    this.setButtonIdle('validate')
    this.updateSubmitButtonState()
  }

  validate(event) {
    event.preventDefault()

    if (!this.fileInputTarget?.files?.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      alert('Please select a valid XLSX, XLS, or CSV file')
      return
    }

    this.setButtonLoading('validate', 'Validating...')

    const formData = new FormData()
    formData.append('xlsx_file', file)

    fetch('/admin/groups_bulk/validate_import.json', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => this.parseJson(response))
      .then(({ response, data }) => {
        const toCreate = data.to_create || 0
        const toUpdate = data.to_update || 0
        const errorsCount = data.errors_count || 0
        const hasValidRows = toCreate + toUpdate > 0

        if (response.ok) {
          this.validationPassed = hasValidRows && errorsCount === 0
          this.displayValidationSuccess(data)
        } else {
          this.validationPassed = false
          this.displayValidationErrors(data)
        }
        this.updateSubmitButtonState()
      })
      .catch(error => {
        console.error('Validation error:', error)
        this.displayValidationErrors({
          errors_count: 1,
          errors_by_row: [{ row: null, errors: ['Validation failed due to an unexpected error'] }]
        })
        this.validationPassed = false
        this.updateSubmitButtonState()
      })
      .finally(() => this.setButtonIdle('validate'))
  }

  updateSubmitButtonState() {
    if (!this.submitBtnTarget) return

    if (this.validationPassed) {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.classList.remove('btn-secondary')
      this.submitBtnTarget.classList.add('btn-success')
    } else {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.remove('btn-success')
      this.submitBtnTarget.classList.add('btn-secondary')
    }
  }

  submit(event) {
    event.preventDefault()

    if (!this.fileInputTarget?.files?.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      alert('Please select a valid XLSX, XLS, or CSV file')
      return
    }

    if (!this.validationPassed) {
      alert('Please validate the file first')
      return
    }

    if (!confirm('Are you sure you want to apply this import?')) return

    this.setButtonLoading('submit', 'Importing...')

    const formData = new FormData()
    formData.append('xlsx_file', file)

    fetch('/admin/groups_bulk/apply_import.json', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => this.parseJson(response))
      .then(({ response, data }) => {
        if (response.ok && data.success) {
          this.lastImportData = data
          this.displayResults(data)
        } else {
          this.displayError(data.message || 'Import failed. Please review the spreadsheet and try again.', data)
        }
      })
      .catch(error => {
        console.error('Group import error:', error)
        this.displayError('Import failed due to an unexpected error. Please try again.')
      })
      .finally(() => this.setButtonIdle('submit'))
  }

  displayValidationSuccess(data) {
    if (!this.reportTarget) return

    const toCreate = data.to_create || 0
    const toUpdate = data.to_update || 0
    const errorsCount = data.errors_count || 0
    const notFound = data.not_found || []

    const createSection = toCreate > 0
      ? `<div class="alert alert-success">
          <strong><i class="bi bi-plus-circle me-2"></i>To Create: ${toCreate}</strong>
        </div>`
      : ''

    const updateSection = toUpdate > 0
      ? `<div class="alert alert-info">
          <strong><i class="bi bi-pencil me-2"></i>To Update: ${toUpdate}</strong>
        </div>`
      : ''

    const notFoundSection = notFound.length > 0
      ? `<div class="alert alert-warning">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Not Found (${notFound.length}):</strong>
          ${this.scrollableDiv(notFound.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorSection = errorsCount > 0
      ? `<div class="alert alert-danger">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Validation Errors (${errorsCount}):</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row || []))}
        </div>`
      : ''

    const statusClass = errorsCount > 0 ? 'alert alert-warning' : 'alert alert-success'
    const statusMessage = errorsCount > 0
      ? 'Validation completed with errors. Please fix the errors before importing.'
      : 'Validation passed! Ready to import groups.'

    this.reportTarget.innerHTML = `
      <div class="${statusClass} mb-3">
        <strong>Summary:</strong><br>
        To Create: ${toCreate}; To Update: ${toUpdate}; Errors: ${errorsCount}
        <br><small>${statusMessage}</small>
      </div>
      ${createSection}
      ${updateSection}
      ${notFoundSection}
      ${errorSection}
    `
  }

  displayValidationErrors(data) {
    if (!this.reportTarget) return

    const errorsCount = data.errors_count || 0
    const errorsByRow = data.errors_by_row || []
    const notFound = data.not_found || []

    const notFoundSection = notFound.length > 0
      ? `<div class="alert alert-warning">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Not Found (${notFound.length}):</strong>
          ${this.scrollableDiv(notFound.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorSection = errorsByRow.length > 0
      ? `<div class="alert alert-danger">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(errorsByRow))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger mb-3">
        <strong>Validation Failed</strong><br>
        ${errorsCount} error(s) found. Please fix the errors and try again.
      </div>
      ${notFoundSection}
      ${errorSection}
    `
  }

  displayResults(data) {
    if (!this.reportTarget) return

    const summary = data.summary || {}
    const created = summary.created || 0
    const updated = summary.updated || 0
    const failed = summary.failed || 0

    const successes = data.successes || []
    const failures = data.failures || []
    const notFound = data.not_found || []

    const createdList = successes.filter(s => s.action === 'created')
    const updatedList = successes.filter(s => s.action === 'updated')

    const createdSection = createdList.length > 0
      ? `<div class="alert alert-success">
          <strong><i class="bi bi-plus-circle me-2"></i>Created (${createdList.length}):</strong>
          ${this.scrollableDiv(createdList.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)})`
          ).join('<br>'))}
        </div>`
      : ''

    const updatedSection = updatedList.length > 0
      ? `<div class="alert alert-info">
          <strong><i class="bi bi-pencil me-2"></i>Updated (${updatedList.length}):</strong>
          ${this.scrollableDiv(updatedList.map(item => {
            const changes = item.field_values ? ` - ${JSON.stringify(item.field_values)}` : ''
            return `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)})${changes}`
          }).join('<br>'))}
        </div>`
      : ''

    const failedSection = failures.length > 0
      ? `<div class="alert alert-danger">
          <strong><i class="bi bi-x-circle me-2"></i>Failed (${failures.length}):</strong>
          ${this.scrollableDiv(failures.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const notFoundSection = notFound.length > 0
      ? `<div class="alert alert-warning">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Not Found (${notFound.length}):</strong>
          ${this.scrollableDiv(notFound.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-success mb-3">
        <strong><i class="bi bi-check-circle me-2"></i>Import Completed!</strong><br>
        Created: ${created}; Updated: ${updated}; Failed: ${failed}
      </div>
      ${createdSection}
      ${updatedSection}
      ${notFoundSection}
      ${failedSection}
    `

    this.validationPassed = false
    this.updateSubmitButtonState()
  }

  displayError(message, data = {}) {
    if (!this.reportTarget) return

    const failures = data.failures || []
    const notFound = data.not_found || []
    const errorsByRow = data.errors_by_row || []

    const failedSection = failures.length > 0
      ? `<div class="alert alert-danger mt-2">
          <strong>Failures:</strong>
          ${this.scrollableDiv(failures.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const notFoundSection = notFound.length > 0
      ? `<div class="alert alert-warning mt-2">
          <strong>Not Found:</strong>
          ${this.scrollableDiv(notFound.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorSection = errorsByRow.length > 0
      ? `<div class="alert alert-danger mt-2">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(errorsByRow))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong><i class="bi bi-x-circle me-2"></i>Import Failed</strong><br>
        ${this.escape(message)}
      </div>
      ${failedSection}
      ${notFoundSection}
      ${errorSection}
    `
  }

  // Utility methods
  setButtonLoading(type, text) {
    const btn = type === 'submit' ? this.submitBtnTarget : this.validateBtnTarget
    if (!btn) return
    btn.disabled = true
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>${text}`
  }

  setButtonIdle(type) {
    const btn = type === 'submit' ? this.submitBtnTarget : this.validateBtnTarget
    if (!btn) return
    btn.disabled = type === 'submit' ? !this.validationPassed : false
    btn.innerHTML = type === 'submit'
      ? '<i class="bi bi-upload me-2"></i>Import / Update'
      : '<i class="bi bi-check-circle me-2"></i>Validate'
  }

  scrollableDiv(content) {
    return `<div style="max-height: 200px; overflow-y: auto; margin-top: 10px;">${content}</div>`
  }

  formatErrorsList(errors_by_row) {
    if (!Array.isArray(errors_by_row)) return ''
    return errors_by_row.map(rowError => {
      return rowError.row
        ? `Row ${rowError.row}: ${rowError.errors.join('; ')}`
        : rowError.errors.join('; ')
    }).join('<br>')
  }

  escape(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  parseJson(response) {
    return response.json()
      .then(data => ({ response, data }))
      .catch(() => ({ response, data: {} }))
  }
}
